import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  Bot,
  Globe,
  Mic,
  Building,
  Settings,
  Plus,
  Pencil,
  Trash2,
  Play,
  RefreshCw,
  Phone,
  Users,
  FileText,
  Shield,
  Sparkles,
  Check,
  ChevronRight,
  Volume2,
  Languages
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

const AGENT_ICONS = {
  receptionist: Phone,
  sales: Users,
  office: FileText,
  insurance: Shield
};

const AGENT_COLORS = {
  receptionist: "blue",
  sales: "emerald",
  office: "purple",
  insurance: "amber"
};

export default function AgentConfiguration() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [voices, setVoices] = useState({ male: [], female: [] });
  const [agentTypes, setAgentTypes] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  
  // Form state
  const [formData, setFormData] = useState({
    agent_type: "",
    name: "",
    // Language
    primary_language: "en-CA",
    dialect: "Standard",
    secondary_language: "",
    fallback_language: "en-US",
    // Voice
    voice_gender: "female",
    voice_id: "rachel",
    voice_speed: 1.0,
    // Business
    business_name: "",
    business_type: "",
    country: "Canada",
    city: "",
    business_hours: "",
    timezone: "America/Toronto",
    // Custom
    additional_instructions: "",
    greeting: "",
    closing: ""
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [agentsRes, langRes, voicesRes, typesRes] = await Promise.all([
        fetch(`${API_URL}/api/agents/my-agents`, { headers }),
        fetch(`${API_URL}/api/agents/public/languages`),
        fetch(`${API_URL}/api/agents/public/voices`),
        fetch(`${API_URL}/api/agents/public/agent-types`)
      ]);

      if (agentsRes.ok) {
        const data = await agentsRes.json();
        setAgents(data.agents || []);
      }
      if (langRes.ok) {
        const data = await langRes.json();
        setLanguages(data.languages || []);
      }
      if (voicesRes.ok) {
        const data = await voicesRes.json();
        setVoices(data.voices || { male: [], female: [] });
      }
      if (typesRes.ok) {
        const data = await typesRes.json();
        setAgentTypes(data.agent_types || {});
      }
      
      // Pre-fill business info
      if (user) {
        setFormData(prev => ({
          ...prev,
          business_name: user.name || "",
          business_type: user.industry || ""
        }));
      }
    } catch (e) {
      toast.error("Failed to load configuration data");
    } finally {
      setLoading(false);
    }
  };

  const getDialectsForLanguage = (langCode) => {
    const lang = languages.find(l => l.code === langCode);
    return lang?.dialects || ["Standard"];
  };

  const createAgent = async () => {
    try {
      const res = await fetch(`${API_URL}/api/agents/configure`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          agent_type: formData.agent_type,
          name: formData.name,
          language_config: {
            primary_language: formData.primary_language,
            dialect: formData.dialect,
            secondary_language: formData.secondary_language === "none" ? null : (formData.secondary_language || null),
            fallback_language: formData.fallback_language
          },
          voice_config: {
            provider: "elevenlabs",
            voice_id: formData.voice_id,
            gender: formData.voice_gender,
            speed: formData.voice_speed,
            pitch: 1.0
          },
          business_context: {
            business_name: formData.business_name,
            business_type: formData.business_type,
            country: formData.country,
            city: formData.city,
            business_hours: formData.business_hours,
            timezone: formData.timezone
          },
          custom_prompts: {
            additional_instructions: formData.additional_instructions,
            greeting: formData.greeting,
            closing: formData.closing
          }
        })
      });

      if (res.ok) {
        toast.success("Agent created successfully!");
        setShowCreateDialog(false);
        resetForm();
        loadInitialData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create agent");
      }
    } catch (e) {
      toast.error("Failed to create agent");
    }
  };

  const updateAgent = async () => {
    if (!selectedAgent) return;
    
    try {
      const res = await fetch(`${API_URL}/api/agents/${selectedAgent.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          language_config: {
            primary_language: formData.primary_language,
            dialect: formData.dialect,
            secondary_language: formData.secondary_language === "none" ? null : (formData.secondary_language || null),
            fallback_language: formData.fallback_language
          },
          voice_config: {
            provider: "elevenlabs",
            voice_id: formData.voice_id,
            gender: formData.voice_gender,
            speed: formData.voice_speed,
            pitch: 1.0
          },
          business_context: {
            business_name: formData.business_name,
            business_type: formData.business_type,
            country: formData.country,
            city: formData.city,
            business_hours: formData.business_hours,
            timezone: formData.timezone
          },
          custom_prompts: {
            additional_instructions: formData.additional_instructions
          }
        })
      });

      if (res.ok) {
        toast.success("Agent updated!");
        setShowEditDialog(false);
        loadInitialData();
      } else {
        toast.error("Failed to update agent");
      }
    } catch (e) {
      toast.error("Failed to update agent");
    }
  };

  const deleteAgent = async (agentId) => {
    if (!confirm("Are you sure you want to delete this agent?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/agents/${agentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success("Agent deleted");
        loadInitialData();
      } else {
        toast.error("Failed to delete agent");
      }
    } catch (e) {
      toast.error("Failed to delete agent");
    }
  };

  const openEditDialog = (agent) => {
    setSelectedAgent(agent);
    setFormData({
      agent_type: agent.agent_type,
      name: agent.name,
      primary_language: agent.language_config?.primary_language || "en-CA",
      dialect: agent.language_config?.dialect || "Standard",
      secondary_language: agent.language_config?.secondary_language || "",
      fallback_language: agent.language_config?.fallback_language || "en-US",
      voice_gender: agent.voice_config?.gender || "female",
      voice_id: agent.voice_config?.voice_id || "rachel",
      voice_speed: agent.voice_config?.speed || 1.0,
      business_name: agent.business_context?.business_name || "",
      business_type: agent.business_context?.business_type || "",
      country: agent.business_context?.country || "Canada",
      city: agent.business_context?.city || "",
      business_hours: agent.business_context?.business_hours || "",
      timezone: agent.business_context?.timezone || "America/Toronto",
      additional_instructions: agent.custom_prompts?.additional_instructions || ""
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      agent_type: "",
      name: "",
      primary_language: "en-CA",
      dialect: "Standard",
      secondary_language: "",
      fallback_language: "en-US",
      voice_gender: "female",
      voice_id: "rachel",
      voice_speed: 1.0,
      business_name: user?.name || "",
      business_type: user?.industry || "",
      country: "Canada",
      city: "",
      business_hours: "",
      timezone: "America/Toronto",
      additional_instructions: "",
      greeting: "",
      closing: ""
    });
    setActiveStep(1);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="agent-configuration-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">AI Agents</h1>
          <p className="text-slate-500 mt-1">Configure your AI voice agents with language, voice, and business settings</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-agent-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Agent
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create AI Agent</DialogTitle>
              <DialogDescription>Configure your agent's language, voice, and behavior</DialogDescription>
            </DialogHeader>
            
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 py-4">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-center">
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      activeStep >= step 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {activeStep > step ? <Check className="w-4 h-4" /> : step}
                  </div>
                  {step < 4 && (
                    <div className={`w-12 h-0.5 ${activeStep > step ? 'bg-blue-600' : 'bg-slate-200'}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-8 text-xs text-slate-500 mb-4">
              <span>Agent Type</span>
              <span>Language</span>
              <span>Voice</span>
              <span>Business</span>
            </div>

            {/* Step 1: Agent Type */}
            {activeStep === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(agentTypes).map(([key, type]) => {
                    const Icon = AGENT_ICONS[key] || Bot;
                    const color = AGENT_COLORS[key] || "blue";
                    const colorClasses = {
                      blue: "border-blue-500 bg-blue-50",
                      emerald: "border-emerald-500 bg-emerald-50",
                      purple: "border-purple-500 bg-purple-50",
                      amber: "border-amber-500 bg-amber-50"
                    };
                    
                    return (
                      <div
                        key={key}
                        onClick={() => setFormData(prev => ({ ...prev, agent_type: key }))}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                          formData.agent_type === key 
                            ? colorClasses[color] 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={`w-6 h-6 ${formData.agent_type === key ? `text-${color}-600` : 'text-slate-400'}`} />
                          <div>
                            <p className="font-medium">{type.name}</p>
                            <p className="text-sm text-slate-500">{type.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="space-y-2">
                  <Label>Agent Name</Label>
                  <Input
                    placeholder="e.g., Sarah (Receptionist)"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
              </div>
            )}

            {/* Step 2: Language */}
            {activeStep === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Primary Language
                    </Label>
                    <Select 
                      value={formData.primary_language} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, primary_language: v, dialect: "Standard" }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {languages.map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.name} ({lang.region})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Dialect / Accent</Label>
                    <Select 
                      value={formData.dialect} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, dialect: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getDialectsForLanguage(formData.primary_language).map((dialect) => (
                          <SelectItem key={dialect} value={dialect}>{dialect}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Secondary Language (Optional)</Label>
                    <Select 
                      value={formData.secondary_language} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, secondary_language: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select secondary language" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="none">None</SelectItem>
                        {languages.filter(l => l.code !== formData.primary_language).map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Fallback Language</Label>
                    <Select 
                      value={formData.fallback_language} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, fallback_language: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en-US">English (US)</SelectItem>
                        <SelectItem value="en-CA">English (Canada)</SelectItem>
                        <SelectItem value="en-GB">English (UK)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Voice */}
            {activeStep === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mic className="w-4 h-4" />
                    Voice Gender
                  </Label>
                  <div className="flex gap-4">
                    {["female", "male"].map((gender) => (
                      <button
                        key={gender}
                        onClick={() => setFormData(prev => ({ 
                          ...prev, 
                          voice_gender: gender,
                          voice_id: voices[gender]?.[0]?.id || ""
                        }))}
                        className={`flex-1 p-3 border-2 rounded-lg capitalize ${
                          formData.voice_gender === gender 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200'
                        }`}
                      >
                        {gender}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Voice</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {voices[formData.voice_gender]?.map((voice) => (
                      <button
                        key={voice.id}
                        onClick={() => setFormData(prev => ({ ...prev, voice_id: voice.id }))}
                        className={`p-3 border-2 rounded-lg text-left ${
                          formData.voice_id === voice.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <p className="font-medium">{voice.name}</p>
                        <p className="text-xs text-slate-500">{voice.accent} • {voice.style}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>Speech Speed</span>
                    <span className="text-sm text-slate-500">{formData.voice_speed.toFixed(1)}x</span>
                  </Label>
                  <Slider
                    value={[formData.voice_speed]}
                    onValueChange={([v]) => setFormData(prev => ({ ...prev, voice_speed: v }))}
                    min={0.5}
                    max={2.0}
                    step={0.1}
                  />
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>Slower</span>
                    <span>Faster</span>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Business Context */}
            {activeStep === 4 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Building className="w-4 h-4" />
                      Business Name
                    </Label>
                    <Input
                      value={formData.business_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                      placeholder="Your Company Inc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Business Type</Label>
                    <Input
                      value={formData.business_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, business_type: e.target.value }))}
                      placeholder="e.g., HVAC, Restaurant, Insurance"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Select 
                      value={formData.country} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, country: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Canada">Canada</SelectItem>
                        <SelectItem value="USA">USA</SelectItem>
                        <SelectItem value="UK">United Kingdom</SelectItem>
                        <SelectItem value="UAE">UAE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>City / Region</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="Toronto"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Business Hours</Label>
                    <Input
                      value={formData.business_hours}
                      onChange={(e) => setFormData(prev => ({ ...prev, business_hours: e.target.value }))}
                      placeholder="9 AM - 5 PM EST"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Select 
                      value={formData.timezone} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, timezone: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/Toronto">Eastern (Toronto)</SelectItem>
                        <SelectItem value="America/Vancouver">Pacific (Vancouver)</SelectItem>
                        <SelectItem value="America/New_York">Eastern (New York)</SelectItem>
                        <SelectItem value="America/Los_Angeles">Pacific (LA)</SelectItem>
                        <SelectItem value="Europe/London">GMT (London)</SelectItem>
                        <SelectItem value="Asia/Dubai">GST (Dubai)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Additional Instructions (Optional)</Label>
                  <Textarea
                    value={formData.additional_instructions}
                    onChange={(e) => setFormData(prev => ({ ...prev, additional_instructions: e.target.value }))}
                    placeholder="Any specific instructions for your agent..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            <DialogFooter className="flex justify-between">
              {activeStep > 1 ? (
                <Button variant="outline" onClick={() => setActiveStep(s => s - 1)}>
                  Back
                </Button>
              ) : (
                <div />
              )}
              {activeStep < 4 ? (
                <Button 
                  onClick={() => setActiveStep(s => s + 1)}
                  disabled={activeStep === 1 && (!formData.agent_type || !formData.name)}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={createAgent}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create Agent
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Agents Grid */}
      {agents.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Bot className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium mb-2">No agents configured</h3>
            <p className="text-slate-500 mb-4">Create your first AI agent to get started</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Agent
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((agent) => {
            const Icon = AGENT_ICONS[agent.agent_type] || Bot;
            const color = AGENT_COLORS[agent.agent_type] || "blue";
            
            return (
              <Card key={agent.id} data-testid={`agent-card-${agent.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 text-${color}-600`} />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{agent.name}</CardTitle>
                        <CardDescription>{agentTypes[agent.agent_type]?.name}</CardDescription>
                      </div>
                    </div>
                    <Badge className={agent.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100"}>
                      {agent.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Languages className="w-4 h-4" />
                    {languages.find(l => l.code === agent.language_config?.primary_language)?.name || agent.language_config?.primary_language}
                    {agent.language_config?.dialect && ` (${agent.language_config.dialect})`}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Volume2 className="w-4 h-4" />
                    {agent.voice_config?.voice_id} • {agent.voice_config?.gender}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Building className="w-4 h-4" />
                    {agent.business_context?.business_name || "Not set"}
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditDialog(agent)}>
                      <Pencil className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteAgent(agent.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Agent: {selectedAgent?.name}</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="language" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="language">Language</TabsTrigger>
              <TabsTrigger value="voice">Voice</TabsTrigger>
              <TabsTrigger value="business">Business</TabsTrigger>
            </TabsList>
            <TabsContent value="language" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Primary Language</Label>
                  <Select 
                    value={formData.primary_language} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, primary_language: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {languages.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Dialect</Label>
                  <Select 
                    value={formData.dialect} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, dialect: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getDialectsForLanguage(formData.primary_language).map((dialect) => (
                        <SelectItem key={dialect} value={dialect}>{dialect}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="voice" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Voice</Label>
                <Select 
                  value={formData.voice_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, voice_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...voices.female, ...voices.male].map((voice) => (
                      <SelectItem key={voice.id} value={voice.id}>
                        {voice.name} ({voice.accent})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
            <TabsContent value="business" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Name</Label>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Business Hours</Label>
                  <Input
                    value={formData.business_hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, business_hours: e.target.value }))}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={updateAgent}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
